<template>
  <div>
    <!-- System table -->
    <div class="card"
         qid="systems-section"
         v-if="showTable"
         id="update_systems"
    >
      <div class="card-body" :class="{'pr-0 pl-0': showOnTask}">
        <div class="row">
          <div class="col-sm-6">
            <div class="float-left">
              <h5 class="mb-2" qid="systems-table-title" v-if="!showOnTask">
                {{ $t('activities.system.title') }}
              </h5>

              <span v-if="!showOnTask">
                <span class="cursor-pointer task-label-open mr-2"
                      v-if="getTask('update_systems')"
                >
                  <UpdateSingleTaskModal
                      :task="getTask('update_systems')"
                      @reloadContent="reloadContent"
                      :editIcon="true"
                  ></UpdateSingleTaskModal>
                </span>
                <span v-if="getGuide('update_systems')">
                  <GuideButton :guide="getGuide('update_systems')"></GuideButton>
                </span>
              </span>
            </div>
          </div>
          <div class="col-sm-6 text-right">
            <span v-if="isGranted(permissions.SYSTEM_CREATE)" qid="system-create-button">
              <span class="btn btn-light" @click="$refs.createSystem.show()">
                <font-awesome-icon icon="plus-circle"/> {{ $t('systems.create_button') }}
              </span>
              <SystemCreatePanel ref="createSystem" :open-after="false"></SystemCreatePanel>
            </span>
            <span class="btn btn-success"
                    @click="showCreateSModal()"
                    qid="activity-system-create-button"
                    v-if="isGranted([permissions.ACTIVITY_SYSTEM_CREATE, permissions.ACTIVITY_SYSTEM_CREATE_OWN], activity)"
            >
              <font-awesome-icon icon="plus"/> {{ $t('activities.system.create_button') }}
            </span>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('activities.system.table.name') }}
            </th>
            <th scope="col">
              {{ $t('activities.system.table.system_role') }}
            </th>
            <th scope="col">
              {{ $t('activities.system.table.data_objects') }}
            </th>
            <th class="text-center">
              {{ $t('activities.third_party.table.attachment') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="380"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="object in sTableItems">
              <td>
                <span v-if="object.system && object.system.name && !isOwner(object.system)"
                      qid="system-name"
                >
                  {{ object.system.name }}
                </span>
                <router-link
                    v-if="object.system && object.system.name && isOwner(object.system)"
                    :to="{ name: 'systems.view', params: { slug: object.system.slug, client: object.system.client }}"
                    qid="system-view-page-link"
                >
                  {{object.system.name}}
                </router-link>
              </td>
              <td>
                <span v-if="object.system_role" class="badge" :title="object.system_role">
                  <font-awesome-icon icon="shapes"/>
                  {{ object.system_role | truncate(35) }}
                </span>
                <span class="text-muted cursor-pointer btn btn-sm btn-link mr-2 ml-2"
                      qid="edit-activity-modal"
                      @click="showEditProcessingRoleModal(object)"
                      v-if="isGranted([permissions.ACTIVITY_SYSTEM_EDIT, permissions.ACTIVITY_SYSTEM_EDIT_OWN], activity)"
                >
                  <font-awesome-icon icon="pen" />
                </span>
              </td>
              <td>
                <span @click="showDataObjectsModal(object, item)"
                      class="mr-1 cursor-pointer btn btn-link p-0 cursor-pointer"
                      v-for="item in getUniqueCategories(object)"
                >
                  <span class="badge">
                    {{ $t('classification.' + $options.filters.slugify(item)) }}
                  </span>
                </span>
              </td>
              <td class="text-center">
                <span v-if="object && object.media && object.media.length"
                      class="btn btn-link btn-xs cursor-pointer"
                      @click="openViewSModal(object, true)"
                >
                  <font-awesome-icon icon="paperclip"/>
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ object.updated_at | formatDate }}
                </span>
              </td>
              <td align="right">
                <span class="btn btn-secondary"
                      qid="edit-activity-modal"
                      @click="showEditSModal(object)"
                      v-if="isGranted([permissions.ACTIVITY_SYSTEM_EDIT, permissions.ACTIVITY_SYSTEM_EDIT_OWN], activity)"
                >
                  {{ $t('activities.system.edit_button') }}
                </span>
                <span class="btn btn-secondary"
                      v-if="isGranted([permissions.ACTIVITY_SYSTEM_EDIT, permissions.ACTIVITY_SYSTEM_EDIT_OWN], activity)"
                      qid="system-copy-link"
                      @click="showDataMappingModal(object)"
                >
                  {{ $t('activities.tab.data_mapping') }}
                </span>
                <span v-if="object.system && object.system.name"
                      @click="openViewSModal(object)"
                      qid="system-name-link"
                      class="btn btn-view"
                >
                  <font-awesome-icon icon="eye"/>
                </span>
                <span class="btn btn-delete"
                      qid="delete-activity-modal"
                      @click="showSConfirmDeleteModal(object)"
                      v-if="isGranted([permissions.ACTIVITY_SYSTEM_DELETE, permissions.ACTIVITY_SYSTEM_DELETE_OWN], activity)"
                >
                  <font-awesome-icon icon="trash-alt" />
                </span>
              </td>
            </tr>
            <tr v-if="!sLoading && !sTableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>


    <!-- Delete system modal template -->
    <b-modal ref="delete-ac" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ac-title">
          {{ $t('activities.system.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.system.delete.description') }}
        <span v-if="itemToDelete.system">{{ itemToDelete.system.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.system.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideSConfirmDeleteModal"
                  qid="delete-ac-cancel-button"
        >
          {{ $t('activities.system.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ac-submit-button"
        >
          {{ $t('activities.system.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit system modal -->
    <b-modal ref="edit-system"
             hide-footer
             @hide="hideEditSModal"
             size="md"
    >
      <div class="d-block text-center">
        <h4 qid="update-tp-title">
          {{ $t('activities.system.update.title') }}
          <span v-if="itemObject.system">'{{ itemObject.system.name }}'</span>
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="sEditFormSubmit" qid="create-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-lb-category-label">
              {{ $t('activities.system.create.data_retention_label') }}
            </label>
            <div class="row">
              <div class="col-sm-6">
                <p class="text-normal smaller">
                  <span>{{ $t('activities.system.create.rules_followed_label') }}</span>
                </p>
              </div>
              <div class="col-sm-6 text-right">
                <b-form-radio-group
                  v-model="sForm.rules_followed"
                  :options="radioOptions"
                  qid="integrity-form-radio"
                  class="mt-1"
                ></b-form-radio-group>
              </div>
            </div>
          </b-form-group>
          <b-form-group>
            <label qid="create-s-description-label">
              {{ $t('activities.system.create.description_label') }}
            </label>
            <b-form-textarea
              qid="dd-form-description"
              v-model="sForm.description"
              rows="4"
              max-rows="4"
              :placeholder="$t('activities.system.create.description_label')"
            ></b-form-textarea>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditSModal"
                    qid="create-p-cancel-button"
          >
            {{ $t('systems.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="create-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('systems.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Data mapping modal -->
    <b-modal ref="data-mapping"
             @hide="hideDataMappingModal"
             modal-class="task-modal mapping-modal"
    >
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideDataMappingModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center">
            {{ $t('activities.system.mapping_title') }}
            <span v-if="itemObject.system && !isOwner(itemObject.system)">
              <font-awesome-icon icon="desktop" />
              {{ itemObject.system.name }}
            </span>
            <router-link
                :to="{ name: 'systems.view', params: { slug: itemObject.system.slug }}"
                qid="s-view-page-link"
                v-if="itemObject.system && isOwner(itemObject.system)"
            >
              <font-awesome-icon icon="desktop" class="text-success" />
              {{ itemObject.system.name }}
            </router-link>
          </h5>
          <div class="row mt-2 pt-4 pb-1" v-if="getGuide('update_systems')">
            <div class="col-sm-12">
              <b-form-group>
                <div class="">
                  <div v-if="getGuide('update_systems')" class="mb-2">
                    <GuideButton :guide="getGuide('update_systems')"></GuideButton>
                  </div>
                  <div v-if="wpGuide && wpGuide.id" class="guide-content-container">
                    <p v-if="!showFullGuideDescription" v-html="wpGuide.excerpt.rendered"></p>
                    <p v-if="showFullGuideDescription" v-html="wpGuide.content.rendered"></p>

                    <div class="text-center mt-3" @click="showFullGuideDescription=!showFullGuideDescription">
                      <span class="cursor-pointer" v-if="!showFullGuideDescription">
                        <span class="mr-2">
                          {{ $t('system.view_more') }}
                        </span>
                        <font-awesome-icon icon="chevron-down"/>
                      </span>
                      <span class="cursor-pointer" v-if="showFullGuideDescription">
                        <span class="mr-2">
                          {{ $t('system.view_less') }}
                        </span>
                        <font-awesome-icon icon="chevron-up"/>
                      </span>
                    </div>
                  </div>
                </div>
              </b-form-group>
            </div>
          </div>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="dataMappingFormSubmit" qid="create-ac-form">
        <div>
          <div class="mb-4 mt-3">
            <span class="btn btn-secondary cursor-pointer" @click="showCopyFromModal(itemObject)">
              <font-awesome-icon icon="plus"/>
              {{ $t('activities.system.copy.system_label') }}
            </span>
            <span class="btn btn-secondary cursor-pointer" @click="toggleSearch()">
              <font-awesome-icon icon="search"/>
              {{ $t('system.show_hide_search') }}
            </span>
          </div>
          <div class="row mb-3" v-if="showSearch">
            <div class="col">
              <b-form-group>
                <label qid="edit-lb-category-label">
                  {{ $t('activities.system.create.data_point_label') }}
                </label>
                <div>
                  <b-form-input
                    type="text"
                    v-model="keywordValue"
                    qid="search-do-input"
                    v-on:input="debounceInput"
                    :placeholder="$t('activities.third_party.data_objects.search_placeholder')"
                  ></b-form-input>
                </div>
              </b-form-group>
            </div>

          </div>
          <!-- <div class="row">
            <div class="col">
              <b-form-group v-if="!hideSearch">
                <label qid="edit-lb-category-label">
                  {{ $t('activities.system.create.data_point_label') }}
                </label>
                <div>
                  <b-form-input
                    type="text"
                    v-model="keywordValue"
                    qid="search-do-input"
                    v-on:input="debounceInput"
                    :placeholder="$t('activities.third_party.data_objects.search_placeholder')"
                  ></b-form-input>
                </div>
              </b-form-group>
            </div>
           <div class="col-1">

            </div>
            <div class="col">
              <b-form-group>
                <label qid="edit-s-data-label">
                  {{ $t('activities.system.create.data_label') }}
                </label>
                <div>
                  <b-form-input
                    type="text"
                    v-model="keywordFilterValue"
                    qid="search-do-input"
                    v-on:input="filterSelectedData"
                    :placeholder="$t('activities.third_party.data_objects.filter_placeholder')"
                  ></b-form-input>
                </div>
              </b-form-group>
            </div> -->
          <div class="row">
            <div class="col">
              <!-- <div>
                <label class="float-right cursor-pointer ml-3"
                       @click="unselectAll"
                       qid="system-unselect-button"
                >
                  {{ $t('system.unselect_all') }}
                </label>
                <label class="float-right cursor-pointer"
                       @click="selectAll"
                       qid="system-select-button"
                >
                  {{ $t('system.select_all') }}
                </label>
              </div>
              <br> -->

              <div @click="showRelatedFunction()" class="mb-1 cursor-pointer">
                <font-awesome-icon :icon="['far', 'square']" style="color:#BCDAD4;margin-bottom:1px;" v-if="!showOnlyRelated"/>
                <font-awesome-icon icon="check-square" style="color:#2DBA9B;margin-bottom:1px;" v-if="showOnlyRelated" />
                {{ $t('system.show_only_related_data') }}
                (<span id="related-number" style="display:inline-block;text-align:center;">{{ selectedDataObjects.length }}</span>)
              </div>

              <div class="form-control scrollable-height-430"
                   qid="tp-source-data-objects"
              >
                <div>
                  <div class="" v-for="(item, index) in DOItems">
                    <div class="data-object-div" :id="'data-object-' + index"
                         @click="selectDataObject(index, item)"
                         :class="{'data-object-div-selected': item.selected}"
                    >
                      <b-popover
                          :target="'data-object-' + index"
                          boundary="viewport"
                          triggers="hover"
                          placement="top"
                          :delay="{ show: 500, hide: 0 }"
                      >
                        <div v-if="item.name" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.name') }}:
                          </span>
                          <br>
                          {{ item.name }}
                          <br>
                        </div>

                        <div v-if="item.description" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.description') }}:
                          </span>
                          <br>
                          {{ item.description | truncate(200)}}
                          <br>
                        </div>

                        <div v-if="item.category" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.category') }}:
                          </span>
                          <br>
                          <span v-if="item.category" class="mr-3">
                            <font-awesome-icon icon="shapes" class="mr-1" />
                            {{ item.category }}
                          </span>
                          <br>
                        </div>

                        <div v-if="item.classification" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.classification') }}:
                          </span>
                          <br>
                          <span class="badge pl-0 mr-3">
                            <font-awesome-icon icon="exclamation-triangle" class="risk-0 mr-2" v-if="item.classification == 'N/A'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="item.classification == 'Generic art. 6'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="item.classification == 'National identification number art. 87'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="item.classification == 'Sensitive art. 9' || item.classification == 'Criminal art. 10'"/>
                            {{ $t('classification.' + $options.filters.slugify(item.classification)) }}
                          </span>
                          <br>
                        </div>

                        <div v-if="item.business_classification" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.business_classification') }}:
                          </span>
                          <br>
                          <span class="badge pl-0 mr-3" v-if="item.business_classification">
                            <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="item.business_classification == 'Unclassified'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="item.business_classification == 'Confidential'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="item.business_classification == 'Top secret' || item.business_classification == 'Secret'"/>
                            {{ $t('classification.' + $options.filters.slugify(item.business_classification)) }}
                          </span>
                          <br>
                        </div>

                        <div v-if="item.tags_translated && item.tags_translated.length">
                          <span class="text-bold">
                            {{ $t('processes.table.tags') }}:
                          </span>
                          <br>
                          <span v-for="tag in item.tags_translated" qid="p-tags-list">
                            <span class="badge" :title="tag.name_translated">
                              <font-awesome-icon icon="tag"/>
                              {{ tag.name_translated }}
                            </span>
                          </span>
                          <br>
                        </div>

                      </b-popover>

                      <font-awesome-icon :icon="['far', 'square']" style="color:#BCDAD4;" v-if="!item.selected" class="mr-2"/>
                      <font-awesome-icon icon="check-square" style="color:#2DBA9B;" v-if="item.selected" class="mr-2"/>
                      <!-- <b-form-checkbox
                        v-model="item.selected"
                        name="checkbox-disable"
                        :value="true"
                        :unchecked-value="false"
                        style="display:inline;pointer-events:none;"
                      ></b-form-checkbox> -->
                      <span>{{ item.name | truncate(20)}}</span>
                    </div>
                  </div>
                  <div class="text-center table-item-div" v-if="!loading && !DOItems.length">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </div>
                </div>
              </div>
              <!-- <div class="text-muted mt-2 mb-3">
                {{ $t('activities.system.create.tip_add_label') }}
              </div> -->
            </div>
            <!-- <div class="col-1">
              <b-button class="mt-200 btn-block"
                        variant="success"
                        @click="addData"
                        size="sm"
                        qid="system-select-button"
              >
                {{ $t('system.add') }} >
              </b-button>
            </div>
            <div class="col">
              <div>
                <label class="float-right cursor-pointer ml-3"
                       @click="unrelateAll"
                       qid="system-unselect-button"
                >
                  {{ $t('system.unselect_all') }}
                </label>
                <label class="float-right cursor-pointer"
                       @click="relateAll"
                       qid="system-select-button"
                >
                  {{ $t('system.select_all') }}
                </label>
                <br>
                <div class="form-control scrollable-height-430" qid="tp-source-data-objects">
                  <div>
                    <div class="table-item-div cursor-pointer"
                         v-for="(itemDO, index) in allDataObjectsFiltered"
                         @click="handleDO(index, itemDO)"
                         :class="{'table-item-div-selected': itemDO.related}"
                    >
                      <div class="text-bold">
                        {{ itemDO.name }}
                      </div>
                      <div>
                        <span v-if="itemDO.category" class="mr-3">
                          <font-awesome-icon icon="shapes" class="mr-1" />
                          {{ itemDO.category }}
                        </span>
                        <span class="mr-3">
                          <span v-if="itemDO.tags_translated && itemDO.tags_translated.length" qid="activity-tags-list">
                            <span v-for="tag in getReducedTags(itemDO.tags_translated)" :title="tag.name_translated" qid="p-tags-list">
                              <span class="badge" :title="tag.name_translated">
                                <font-awesome-icon icon="tag"/>
                                {{ tag.name_translated | truncate(35) }}
                              </span>
                            </span>
                            <span v-if="itemDO.tags_translated.length > 1"
                                  class="badge cursor-pointer"
                                  @click="openTagsModal(itemDO, $event)"
                            >
                              <font-awesome-icon icon="tags"/>
                              + {{ itemDO.tags_translated.length - 1 }}
                            </span>
                          </span>
                        </span>
                        <span class="badge pl-0 mr-3">
                          <font-awesome-icon icon="exclamation-triangle" class="risk-0 mr-2" v-if="itemDO.classification == 'N/A'"/>
                          <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="itemDO.classification == 'Generic art. 6'"/>
                          <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="itemDO.classification == 'National identification number art. 87'"/>
                          <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="itemDO.classification == 'Sensitive art. 9' || itemDO.classification == 'Criminal art. 10'"/>
                          {{ $t('classification.' + $options.filters.slugify(itemDO.classification)) }}
                        </span>
                        <span class="badge pl-0 mr-3" v-if="itemDO.business_classification">
                          <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="itemDO.business_classification == 'Unclassified'"/>
                          <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="itemDO.business_classification == 'Confidential'"/>
                          <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="itemDO.business_classification == 'Top secret' || itemDO.business_classification == 'Secret'"/>
                          {{ $t('classification.' + $options.filters.slugify(itemDO.business_classification)) }}
                        </span>
                      </div>
                    </div>
                    <div class="text-center table-item-div" v-if="!allDataObjectsFiltered.length">
                      <h5 class="mt-3">
                        {{ $t('system.no_results') }}
                      </h5>
                    </div>
                  </div>
                </div>
                <div class="text-muted mt-2">
                  {{ $t('activities.system.create.tip_label') }}
                </div>
                <div>
                  <span class="color-primary mr-2 legend-icon-container">
                    <span class="legend legend-primary"></span>
                  </span>
                  <span>{{ $t('system.related') }}</span>
                  <span class="color-white ml-4 mr-2 legend-icon-container">
                    <span class="legend legend-white"></span>
                  </span>
                  <span>{{ $t('system.not_related') }}</span>
                </div>
              </div>
            </div> -->
          </div>
        </div>
      </b-form>
      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">
                <div v-if="isGranted(permissions.DATA_OBJECT_CREATE)"
                     qid="data-object-system-create-button"
                     class="text-left mt-2"
                >
                  <DataObjectCreateForm @updated="loadDO()"></DataObjectCreateForm>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-2 mb-2"
                            variant="secondary"
                            @click="mappingHide"
                            qid="create-ac-cancel-button"
                  >
                    {{ $t('activities.third_party.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="mt-2 mb-2 ml-3 mr-0"
                            :disabled="buttonDisabled"
                            variant="success"
                            @click="dataMappingFormSubmit()"
                            qid="create-ac-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('activities.third_party.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </template>
    </b-modal>


    <!-- Copy from modal -->
    <b-modal ref="copy-from"
             @hide="hideCopyFromModal"
             size="sm"
             modal-class="task-modal mapping-modal"
    >

      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="copyHide" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center">
            {{ $t('activities.system.copy.modal_title') }}
          </h5>
          <div class="row mt-2 pt-4 pb-1" v-if="getGuide('update_systems')">
            <div class="col-sm-12">
              <b-form-group>
                <div class="">
                  <div v-if="getGuide('update_systems')" class="mb-2">
                    <GuideButton :guide="getGuide('update_systems')"></GuideButton>
                  </div>
                  <div v-if="wpGuide && wpGuide.id" class="guide-content-container">
                    <p v-if="!showFullCopyGuideDescription" v-html="wpGuide.excerpt.rendered"></p>
                    <p v-if="showFullCopyGuideDescription" v-html="wpGuide.content.rendered"></p>

                    <div class="text-center mt-3" @click="showFullCopyGuideDescription=!showFullCopyGuideDescription">
                      <span class="cursor-pointer" v-if="!showFullCopyGuideDescription">
                        <span class="mr-2">
                          {{ $t('system.view_more') }}
                        </span>
                        <font-awesome-icon icon="chevron-down"/>
                      </span>
                      <span class="cursor-pointer" v-if="showFullCopyGuideDescription">
                        <span class="mr-2">
                          {{ $t('system.view_less') }}
                        </span>
                        <font-awesome-icon icon="chevron-up"/>
                      </span>
                    </div>
                  </div>
                </div>
              </b-form-group>
            </div>
          </div>
        </div>

      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="copyFromFormSubmit" qid="create-ac-form">
        <div>
          <div class="row mb-3">
            <div class="col">
              <b-form-group>
                <label qid="create-ac-pg-label">
                  {{ $t('activities.system.copy.system_label') }}
                </label>
                <multiselect
                    v-model="selectedTP"
                    :options="copyOptions"
                    :placeholder="$t('activities.system.copy.system_placeholder')"
                    label="name"
                    group-values="items"
                    group-label="group"
                    :group-select="false"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    @select="selectCopyItem"
                    @remove="deselectCopyItem"
                    track-by="track_name"
                    qid="create-ac-form-ac"
                ></multiselect>
              </b-form-group>
            </div>
          </div>
          <div class="row">
            <div class="col">

              <div>
                <label class="float-right cursor-pointer"
                       @click="selectAllCopy"
                       qid="system-select-button"
                >
                  {{ $t('system.select_all') }}
                </label>
              </div>
              <br>

              <div class="form-control scrollable-height-430 pt-3"
                   qid="tp-source-data-objects"
              >
                <div>
                  <div class="" v-for="(item, index) in copyItems">
                    <div class="data-object-div" :id="'copy-object-' + index"
                         @click="selectCopyObject(index, item)"
                         :class="{'data-object-div-selected': item.selected}"
                    >
                      <b-popover
                          :target="'copy-object-' + index"
                          boundary="viewport"
                          triggers="hover"
                          placement="top"
                          :delay="{ show: 500, hide: 0 }"
                      >
                        <div v-if="item.name" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.name') }}:
                          </span>
                          <br>
                          {{ item.name }}
                          <br>
                        </div>

                        <div v-if="item.description" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.description') }}:
                          </span>
                          <br>
                          {{ item.description | truncate(200)}}
                          <br>
                        </div>

                        <div v-if="item.category" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.category') }}:
                          </span>
                          <br>
                          <span v-if="item.category" class="mr-3">
                            <font-awesome-icon icon="shapes" class="mr-1" />
                            {{ item.category }}
                          </span>
                          <br>
                        </div>

                        <div v-if="item.classification" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.classification') }}:
                          </span>
                          <br>
                          <span class="badge pl-0 mr-3">
                            <font-awesome-icon icon="exclamation-triangle" class="risk-0 mr-2" v-if="item.classification == 'N/A'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="item.classification == 'Generic art. 6'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="item.classification == 'National identification number art. 87'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="item.classification == 'Sensitive art. 9' || item.classification == 'Criminal art. 10'"/>
                            {{ $t('classification.' + $options.filters.slugify(item.classification)) }}
                          </span>
                          <br>
                        </div>

                        <div v-if="item.business_classification" class="mt-2">
                          <span class="text-bold">
                            {{ $t('maintenance.data_objects.table.business_classification') }}:
                          </span>
                          <br>
                          <span class="badge pl-0 mr-3" v-if="item.business_classification">
                            <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="item.business_classification == 'Unclassified'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="item.business_classification == 'Confidential'"/>
                            <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="item.business_classification == 'Top secret' || item.business_classification == 'Secret'"/>
                            {{ $t('classification.' + $options.filters.slugify(item.business_classification)) }}
                          </span>
                          <br>
                        </div>

                        <div v-if="item.tags_translated && item.tags_translated.length">
                          <span class="text-bold">
                            {{ $t('processes.table.tags') }}:
                          </span>
                          <br>
                          <span v-for="tag in item.tags_translated" qid="p-tags-list">
                            <span class="badge" :title="tag.name_translated">
                              <font-awesome-icon icon="tag"/>
                              {{ tag.name_translated }}
                            </span>
                          </span>
                          <br>
                        </div>
                      </b-popover>

                      <font-awesome-icon :icon="['far', 'square']" style="color:#BCDAD4;" v-if="!item.selected" class="mr-2"/>
                      <font-awesome-icon icon="check-square" style="color:#2DBA9B;" v-if="item.selected" class="mr-2"/>
                      <!-- <b-form-checkbox
                        v-model="item.selected"
                        name="checkbox-disable"
                        :value="true"
                        :unchecked-value="false"
                        style="display:inline;pointer-events:none;"
                      ></b-form-checkbox> -->
                      <span>{{ item.name | truncate(15)}}</span>
                    </div>
                  </div>
                  <div class="text-center table-item-div" v-if="!loading && !copyItems.length">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-2 mb-2"
                            variant="secondary"
                            @click="copyHide"
                            qid="create-ac-cancel-button"
                  >
                    {{ $t('activities.third_party.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="mt-2 mb-2 ml-3 mr-0"
                            :disabled="buttonDisabled"
                            variant="success"
                            @click="copyFromFormSubmit()"
                            qid="create-ac-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('activities.third_party.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </template>
    </b-modal>


    <!-- Add new system modal -->
    <b-modal ref="add-system" @hide="hideCreateSModal" modal-class="task-modal mapping-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreateSModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center">
            {{ $t('activities.system.create.title') }}
          </h5>
        </div>
      </template>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="sFormSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-ac-pg-label">
              {{ $t('activities.system.create.system_label') }}
              <span class="text-red">*</span>
            </label>
            <multiselect
                v-model="selectedS"
                :options="sItems"
                :placeholder="$t('activities.system.create.system_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadS"
                track-by="slug"
                qid="create-ac-form-ac"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedS && selectedS.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showSDescription = !showSDescription"
                    qid="expand-system-description"
              >
                {{$t('activities.system.create.system_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showSDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showSDescription"/>
            </span>
          </div>
          <div v-if="selectedS && selectedS.description && showSDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedS.description }}
            </span>
          </div>
        </div>
      </b-form>
      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideCreateSModal"
                            qid="create-ac-cancel-button"
                  >
                    {{ $t('activities.system.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            @click="sFormSubmit"
                            :disabled="buttonDisabled"
                            qid="create-ac-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('activities.system.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </template>
    </b-modal>


    <!-- Show data objects modal -->
    <b-modal ref="view-data-objects" hide-footer @hide="hideDataObjectsModal">
      <div class="d-block text-center">
        <h4 qid="delete-tp-title">
          {{ $t('maintenance.third_parties.data_objects.title') }}
        </h4>
      </div>
      <div class="mt-5" v-if="filteredDataObjects && filteredDataObjects.length">
        <div v-for="dataObject in filteredDataObjects">
          <div class="mb-4">
            <span class="badge badge-info float-right">
              {{ $t('classification.' + $options.filters.slugify(dataObject.classification )) }}
            </span>
            <span>{{ dataObject.name }}</span>
          </div>
        </div>
      </div>
      <div class="float-right">
        <b-button class="mt-3"
                  variant="success"
                  @click="hideDataObjectsModal"
                  qid="delete-tp-cancel-button"
        >
          {{ $t('maintenance.third_parties.data_objects.close') }}
        </b-button>
      </div>
    </b-modal>



    <!-- Edit processing role modal -->
    <b-modal ref="edit-processing-role" hide-footer @hide="hideEditProcessingRoleModal">
      <div class="d-block text-center">
        <h4 qid="delete-tp-title">
          {{ $t('activities.system.system_role_modal_title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editProcessingRoleFormSubmit" qid="create-p-form">
        <div class="form-container">
          <b-form-group :class="{'multiselect--show-input': systemRoleInput.length}">
            <label qid="edit-lb-category-label">
             {{ $t('activities.system.create.system_role_label') }}
            </label>
            <multiselect
                v-model="selectedSystemRole"
                :options="systemRoleItems"
                :placeholder="$t('activities.system.create.system_role_placeholder')"
                :preserveSearch="true"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="handleCategoryInput"
                track-by="system_role"
                label="system_role"
                qid="edit-as-form-system_role"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.legal_basis.create.category_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditProcessingRoleModal"
                    qid="create-p-cancel-button"
          >
            {{ $t('projects.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('projects.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <SystemViewModal
        :object="selectedS"
        :showModal="viewSModal"
        @closeViewModal="handleCloseViewSModal"
        v-if="selectedS"
        :active="showDocuments"
    ></SystemViewModal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import roleCheck from "@/util/ACL/roleCheck"
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import DataObjectCreateForm from '@/views/DataObjects/DataObjectCreateForm.vue';
  import SystemCreatePanel from '@/views/Systems/page/SystemCreatePanel.vue';
  import SystemViewModal from '@/views/Systems/SystemViewModal.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';


  export default {
    name: 'SystemTable',
    data: function() {
      return {
        sForm: {
          system_role: '',
          rules_followed: '',
          data_objects: [],
          description: '',
          links: []
        },

        activity: {},

        // Systems
        sTableItems: [],
        selectedS: [],
        viewSModal: false,
        selectedSSort: '',
        selectedSDirection: '',
        sItems: [],
        sLoading: true,
        loading: true,
        copySystemItems: [],
        copyThirdPartyItems: [],
        sValidationShow: false,
        updateValidationList: false,
        showFullGuideDescription: false,
        showFullCopyGuideDescription: false,

        copyOptions: [
          {
            group: this.$t('activities.system.copy.general_label'),
            items: []
          },
          {
            group: this.$t('activities.system.copy.systems_label'),
            items: []
          },
          {
            group: this.$t('activities.system.copy.sources_label'),
            items: []
          },
          {
            group: this.$t('activities.system.copy.receivers_label'),
            items: []
          }
        ],

        // categories
        systemRoleItems: [],
        systemRoleInput: '',
        selectedSystemRole: [],

        // Data objects
        allDataObjects: [],
        allDataObjectsFiltered: [],
        dataObjects: [],
        systemDataObjects: [],
        newDataObjects: [],
        DOItems: [],
        copyItems: [],
        selectedDataObjects: [],
        selectedCopyObjects: [],
        availableDataObjects: [],
        selectedItem: {},
        showTagsModal: false,
        selectedTP: [],

        error: '',
        loadingItems: false,
        itemToDelete: {},
        itemObject: {},
        reCertificationOptions: [],
        provisioningOptions: [],
        selectedRC: [],
        selectedP: [],
        validationOptions: [],
        validation: {},
        disabledValidationOptions: [],
        showSDescription: false,
        radioOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0}
        ],
        buttonDisabled: false,
        keywordValue: '',
        keywordFilterValue: '',
        showSelectionTable: false,
        selectedFiles: [],
        inputLink: '',
        inputLinkName: '',
        showDocuments: false,
        filteredDataObjects: [],
        showTable: false,
        hideSearch: false,
        showMore: false,
        page: 1,
        myGuides: [],
        myTasks: [],
        viewVendorModal: false,
        showCopyDropdown: false,
        showSearch: false,
        showOnlyRelated: false,
        wpGuide: {},
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      showOnTask: {},
      task: {}
    },
    components: {
      Avatar,
      ValidationList,
      ValidationForm,
      DataObjectCreateForm,
      SystemCreatePanel,
      SystemViewModal,
      UpdateSingleTaskModal,
      GuideButton,
      ShowMoreModal
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getUser() {
        return this.$store.state.user
      },
      getFiles() {
        return this.selectedFiles
      },
      getWordpressLink() {
        return this.$store.state.wordpressLink
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        if (this.activity && this.activity.id) {
          this.loadSR();
        }
      },
      object: function (newValue) {
        if (newValue && newValue.id) {
          this.activity = newValue
          this.showTable = true;
          this.loadTasks('update_systems');
          this.loadSystems();
          this.loadSR();
        }
      },
      showOnlyRelated: function (newValue) {
        if (newValue) {
          this.keywordValue = ''
          this.selectedDataObjects = _.sortBy(this.selectedDataObjects, 'name');
          this.DOItems =  _.cloneDeep(this.selectedDataObjects);
        } else {
          this.loadDO(this.keywordValue)
        }
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.activity.id && this.$store.state.updatedObject.who_am_i == this.activity.who_am_i) {
              this.activity = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_systems')
        }
      },
    },
    methods: {
      debounceInput: _.debounce(function() {
        let self = this;
        if (this.keywordValue && this.keywordValue.length) {
          this.showOnlyRelated = false;
        }

        this.loadDO(this.keywordValue)

      }, 400),
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      openGuide() {
        if (this.wpGuide && this.wpGuide.link) {
          window.open(this.wpGuide.link, '_blank')
        }
      },
      toggleCopyDropdown() {
        this.showCopyDropdown = !this.showCopyDropdown
      },
      toggleSearch() {
        this.showSearch = !this.showSearch
      },
      showRelatedFunction() {
        this.showOnlyRelated = !this.showOnlyRelated
      },
      isOwner(object) {
        let self = this;
        let allow = false;
        let existingUser = _.find(object.users, function(user) {
          return user.id == self.getUser.id
        });

        if (existingUser) {
          allow = true
        } else {
          if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
            allow = true
          }
        }

        return allow
      },
      showEditProcessingRoleModal(object) {
        this.itemObject = object
        let self = this;

        this.selectedSystemRole = []
        if (object.system_role) {
          _.find(this.systemRoleItems, function(item) {
            if (item.system_role == object.system_role) {
              self.selectedSystemRole = item;
            }
          });
        }

        this.$refs['edit-processing-role'].show()
      },
      hideEditProcessingRoleModal() {
        this.error = ''
        this.itemObject = []
        this.selectedSystemRole = []
        this.systemRoleInput = ''
        this.loadSR();
        this.loadSystems()
        this.$refs['edit-processing-role'].hide()
      },
      editProcessingRoleFormSubmit(evt) {

        let self = this;
        evt.preventDefault();

        if (this.systemRoleInput && this.systemRoleInput.length) {
          this.sForm.system_role = this.systemRoleInput
        } else {
          if (this.selectedSystemRole && this.selectedSystemRole.system_role) {
            this.sForm.system_role = this.selectedSystemRole.system_role
          } else {
            this.sForm.system_role = '';
          }
        }

        self.sForm.data_objects = []
        _.forEach(this.itemObject.data_objects, function (dataObject) {
          self.sForm.data_objects.push(dataObject.id)
        })

        this.buttonDisabled = true;
        piincHttp.put('activities/' + this.activity.slug + '/systems/' + this.itemObject.id, this.sForm).then(function(response) {
          self.$toastr('success', self.$t('activities.third_party.update.success'))
          self.hideEditProcessingRoleModal()
          self.$emit('updated');
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      openTagsModal(object, evt) {
        this.selectedItem = object
        this.showTagsModal = true
        evt.stopPropagation();
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 1) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 1);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      reloadContent() {
        this.loadTasks('update_systems');
        this.loadSystems();
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.activity.who_am_i,
              model_id: this.activity.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      setValidation(object) {
        this.validation = object
        this.updateValidationList = false;
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Activity' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Activity',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
            piincHttp.get(self.getWordpressLink + self.myGuides[0].external_id).then(function(response) {
              self.wpGuide = response.data
            }, function() {});
          }
        }, function() {});
      },
      resetFile() {
        let imagefile = document.querySelector('#file');
        imagefile.value = '';
      },
      onFileChange() {
        let files = document.querySelector('#file');
        // in order for v-for to re render, we need to set array to empty
        this.selectedFiles = [];
        // And then push item by item into that array
        for (var i = 0; i < files.files.length; i++) {
          this.selectedFiles.push(files.files[i])
        }
      },
      handleLink() {
        // If there's a link in input, add it to links array
        if (this.inputLink !== '') {
          if (!this.inputLink.startsWith('http')) {
            this.inputLink = 'http://' + this.inputLink
          }
          this.sForm.links.push({url: this.inputLink, name: this.inputLinkName})
          // Reset link input form
          this.inputLink = ''
          this.inputLinkName = ''
        }
      },
      removeLinkItem(index) {
        // Remove link item from the links array
        this.sForm.links.splice(index, 1);
      },
      addData() {
        let self = this;
        _.forEach(this.DOItems, function (item) {
          if (item.selected) {
            self.updateDOList(item)
          }
        })
        this.unselectAll()
        if (this.selectedS && this.selectedS.id) {
          this.selectCopyItem(this.selectedS)
        } else {
          this.loadDO()
        }
      },
      filterSelectedData() {
        let self = this;
        this.allDataObjectsFiltered = [];
        if (self.keywordFilterValue) {
          _.forEach(this.allDataObjects, function (item) {
            if (
              item.name.toLowerCase().indexOf(self.keywordFilterValue.toLowerCase()) !== -1 ||
              (item.category && item.category.toLowerCase().indexOf(self.keywordFilterValue.toLowerCase()) !== -1) ||
              (item.classification && item.classification.toLowerCase().indexOf(self.keywordFilterValue.toLowerCase()) !== -1)
            ) {
              self.allDataObjectsFiltered.push(item)
            }
          })
        } else {
          this.allDataObjectsFiltered = _.cloneDeep(this.allDataObjects);
        }
      },
      resetFields() {
        this.selectedRC = null
        this.selectedP = null
      },
      handleCategoryInput(input) {
        this.systemRoleInput = input;
      },
      setValidation(object) {
        this.validation = object
      },
      selectCopyItem(object) {
        this.showMore = false;
        this.selectedCopyObjects = []
        let self = this
        this.copyItems = []
        let results = object.data_objects
        _.forEach(results, function (item) {
          if (_.findIndex(self.allDataObjects, function(o) { return o.id == item.id }) == -1) {
            item.selected = false
            self.copyItems.push(item)
          } else {
            let index = _.findIndex(self.allDataObjects, function(o) { return o.id == item.id })
            if ( index >= 0) {
              if (!self.allDataObjects[index].related) {
                item.selected = false
                self.copyItems.push(item)
              }
            }
          }
        })
      },
      deselectCopyItem(object) {
        this.loadDO();
        this.hideSearch = false;
      },
      showDataObjectsModal(object, classification) {
        let self = this;
        this.selectedS = object

        if (!object) {
          this.availableDataObjects = [];
        }

        _.forEach(object.data_objects, function (item) {
          if (item.classification == classification) {
            self.filteredDataObjects.push(item)
          }
        })

        this.$refs['view-data-objects'].show()
      },
      hideDataObjectsModal() {
        this.selectedS = {}
        this.filteredDataObjects = []
        this.$refs['view-data-objects'].hide()
      },
      getUniqueCategories(object) {
        let categories = []
        // Loop through all data objects in third party
        if (object.data_objects && object.data_objects.length) {
          _.forEach(object.data_objects, function (item) {
            categories.push(item.classification)
          })
          // Return unique array of categories in data objects
          return _.uniq(categories);
        }
      },
      showCreateSModal() {
        this.loadS();
        this.$refs['add-system'].show()
      },
      hideCreateSModal() {
        this.error = ''
        this.sForm.links = [];
        this.selectedFiles = [];
        this.selectedS = []
        this.loadContent()
        this.$refs['add-system'].hide()
      },
      showDataMappingModal(object) {
        let self = this;
        let inArray = false;
        this.itemObject = object
        this.selectedDataObjects = []
        this.sForm.data_objects = [];
        this.availableDataObjects = [];

        _.forEach(object.data_objects, function (dataObject) {
           dataObject.selected = true;
           self.selectedDataObjects.push(dataObject)
        })
        this.selectedDataObjects = _.sortBy(this.selectedDataObjects, 'name');

        this.loadDO();
        // this.systemDataObjects.sort(function(a, b) {
        //   var nameA = a.name.toUpperCase(); // ignore upper and lowercase
        //   var nameB = b.name.toUpperCase(); // ignore upper and lowercase
        //   if (nameA < nameB) {
        //     return -1;
        //   }
        //   if (nameA > nameB) {
        //     return 1;
        //   }
        //   // names must be equal
        //   return 0;
        // });
        //
        // if (this.dataObjects && this.dataObjects.length) {
        //   _.forEach(this.dataObjects, function (dataObject) {
        //     dataObject.related = true;
        //     self.allDataObjects.push(dataObject)
        //   })
        //   self.allDataObjects.sort(function(a, b) {
        //     var nameA = a.name.toUpperCase(); // ignore upper and lowercase
        //     var nameB = b.name.toUpperCase(); // ignore upper and lowercase
        //     if (nameA < nameB) {
        //       return -1;
        //     }
        //     if (nameA > nameB) {
        //       return 1;
        //     }
        //     // names must be equal
        //     return 0;
        //   });
        // }
        //
        // if (this.systemDataObjects && this.systemDataObjects.length) {
        //   _.forEach(this.systemDataObjects, function (dataObject) {
        //     dataObject.related = false;
        //     inArray = false;
        //     _.forEach(self.allDataObjects, function (item, index) {
        //       if (item.id == dataObject.id) {
        //         // if item is already in array
        //         inArray = true;
        //       }
        //     })
        //     if (!inArray) {
        //       // If not in array, push it in array as related
        //       self.allDataObjects.push(dataObject)
        //     }
        //   })
        // }
        //
        // this.allDataObjectsFiltered = _.cloneDeep(this.allDataObjects);
        //
        // self.copyThirdPartyItems = self.copyThirdPartyItems.filter(function(item) {
        //     return item.id != object.id;
        // });
        //
        // piincHttp.get('activities/' + this.activity.slug + '/third-party-receivers', { params:
        //   {
        //     client: this.getClient.slug
        //   }
        // }).then(function(response) {
        //   _.forEach(response.data, function (item) {
        //     item.name = item.third_party.vendors[0].name + ' - ' + item.third_party.name + ' - Third party ' + item.type
        //     item.slug = item.third_party.slug
        //     self.copyThirdPartyItems.push(item)
        //   })
        //   self.copyThirdPartyItems = self.copyThirdPartyItems.filter(function(item) {
        //       return item.id != object.id;
        //   });
        //   piincHttp.get('activities/' + self.activity.slug + '/systems', { params:
        //     {
        //       client: self.getClient.slug
        //     }
        //   }).then(function(response) {
        //     _.forEach(response.data, function (item) {
        //       item.name = item.system.name + ' - System'
        //       item.slug = item.system.slug
        //       self.copyThirdPartyItems.push(item)
        //     })
        //   }, function() {});
        // }, function() {});

        this.$refs['data-mapping'].show()
      },
      hideDataMappingModal() {
        this.error = ''
        this.keywordValue = ''
        this.keywordFilterValue = ''
        this.allDataObjects = []
        this.loadSystems()
        this.copyThirdPartyItems = []
        this.itemObject = {}
        this.selectedTP = []
        this.hideSearch = false;
        this.$refs['data-mapping'].hide()
      },
      mappingHide() {
        this.$refs['data-mapping'].hide()
      },
      showCopyFromModal(object) {
        let self = this;
        let inArray = false;
        this.itemObject = object
        this.selectedCopyObjects = []
        this.sForm.data_objects = [];

        // piincHttp.get('activities/' + this.activity.slug + '/third-party-sources', { params:
        //   {
        //     client: this.getClient.slug
        //   }
        // }).then(function(response) {
        //   _.forEach(response.data, function (item) {
        //     item.name = item.third_party.vendors[0].name + ' - ' + item.third_party.name + ' - Third party ' + item.type
        //     item.slug = item.third_party.slug
        //     self.copyThirdPartyItems.push(item)
        //   })
        //   self.copyThirdPartyItems = self.copyThirdPartyItems.filter(function(item) {
        //       return item.id != object.id;
        //   });
        //   piincHttp.get('activities/' + self.activity.slug + '/systems', { params:
        //     {
        //       client: self.getClient.slug
        //     }
        //   }).then(function(response) {
        //     _.forEach(response.data, function (item) {
        //       item.name = item.system.name + ' - System'
        //       item.slug = item.system.slug
        //       self.copyThirdPartyItems.push(item)
        //     })
        //   }, function() {});
        // }, function() {});

        self.copyOptions[0].items = []
        let item = {}
        if (this.itemObject && this.itemObject.system) {
          item.name = this.$t('activities.system.copy.previously_selected_label_system')
          item.track_name = this.$t('activities.system.copy.previously_selected_label_system')
          item.data_objects = this.itemObject.system.data_objects
          self.copyOptions[0].items.push(item)
        }

        piincHttp.get('activities/' + self.activity.slug + '/systems', { params:
          {
            client: self.getClient.slug
          }
        }).then(function(response) {
          self.copyOptions[1].items = []
          _.forEach(response.data, function (item) {
            item.name = item.system.name
            item.track_name = item.system.name + ' - System'
            item.slug = item.system.slug
            if (self.itemObject.id != item.id) {
              self.copyOptions[1].items.push(item)
            }
          })
        }, function() {});

        piincHttp.get('activities/' + this.activity.slug + '/third-party-sources', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.copyOptions[2].items = []
          _.forEach(response.data, function (item) {
            item.name = item.third_party.vendors[0].name + ' - ' + item.third_party.name
            item.track_name = item.third_party.name + ' - ' + item.third_party.name + ' - Third party ' + item.type
            item.slug = item.third_party.slug
            if (self.itemObject.id != item.id) {
              self.copyOptions[2].items.push(item)
            }
          })
        }, function() {});

        piincHttp.get('activities/' + this.activity.slug + '/third-party-receivers', { params:
          {
            client: self.getClient.slug
          }
        }).then(function(response) {
          self.copyOptions[3].items = []
          _.forEach(response.data, function (item) {
            item.name = item.third_party.vendors[0].name + ' - ' + item.third_party.name
            item.track_name = item.third_party.name + ' - ' + item.third_party.name + ' - Third party ' + item.type
            item.slug = item.third_party.slug
            if (self.itemObject.id != item.id) {
              self.copyOptions[3].items.push(item)
            }
          })
        }, function() {});

        this.$refs['copy-from'].show()
      },
      hideCopyFromModal() {
        this.error = ''
        this.keywordValue = ''
        this.copyThirdPartyItems = []
        this.copyItems = []
        this.selectedTP = []
        this.hideSearch = false;
        this.loadDO()
        this.$refs['copy-from'].hide()
      },
      copyHide() {
        this.$refs['copy-from'].hide()
      },
      selectAllCopy() {
        let newDataObjectsArray = []
        let self = this;
        _.forEach(this.copyItems, function (item) {
          item.selected = true;
          newDataObjectsArray.push(item)
          self.selectedCopyObjects.push(item)
        })
        this.copyItems = newDataObjectsArray;
      },
      showSConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-ac'].show()
      },
      hideSConfirmDeleteModal() {
        this.itemToDelete = {}
        this.loadContent()
        this.$refs['delete-ac'].hide()
      },
      openViewSModal(object, showDocuments) {
        if (showDocuments) {
          this.showDocuments = true;
        }
        this.selectedS = object
        this.viewSModal = true
      },
      handleCloseViewSModal () {
        this.selectedS = []
        this.viewSModal = false;
        this.showDocuments = false;
      },
      setCopy(index, item) {
        // Toggle related/not related property
        item.copy = item.copy ? false : true;
        this.$set(this.availableDataObjects, index, item)
      },
      setAllCopy() {
        this.availableDataObjects = []
        _.forEach(this.selectedS.data_objects, function (item) {
          item.copy = true;
        })
        this.availableDataObjects = this.selectedS.data_objects;
      },
      unsetAllCopy() {
        this.availableDataObjects = []
        _.forEach(this.selectedS.data_objects, function (item) {
          item.copy = false;
        })
        this.availableDataObjects = this.selectedS.data_objects;
      },
      showDataObjects(object) {
        this.availableDataObjects = []
        if (object && object.data_objects && object.data_objects.length) {
          this.availableDataObjects = object.data_objects;
        }
      },
      hideDataObjects() {
        this.availableDataObjects = []
      },
      handleDO(index, item, related) {
        let self = this;

        if (item && item.new_item && item.related && !related) {
          this.removeDataItem(item, related)
        } else {
          if (related == undefined) {
            // Toggle related/not related property
            item.related = item.related ? false : true;
          } else {
            item.related = related
          }

          _.forEach(this.allDataObjects, function (object, objectIndex) {
            if (item.id == object.id) {
              self.$set(self.allDataObjects, objectIndex, item)
            }
          })
          this.$set(this.allDataObjectsFiltered, index, item)

          if (this.selectedS && this.selectedS.id) {
            this.selectCopyItem(this.selectedS)
          } else {
            if (related == undefined) {
              this.loadDO()
            }
          }

        }
      },
      selectDataObject(index, item) {
        item.selected = item.selected ? false : true;
        if (item.selected) {
          let foundItem = false;
          _.forEach(this.selectedDataObjects, function (object) {
            if (object.id == item.id) {
              foundItem = true
            }
          })

          if (!foundItem) {
            this.selectedDataObjects.push(item)
          }
        } else {
          let itemIndex = _.findIndex(this.selectedDataObjects, function(o) { return o.id == item.id; });
          this.selectedDataObjects.splice(itemIndex, 1);
        }

        this.$set(this.DOItems, index, item)
        var element = document.getElementById('related-number');

        element.classList.add("bounce-enter-active");
        setTimeout(function() {
          element.classList.remove("bounce-enter-active");
        }, 700)
      },
      selectCopyObject(index, item) {
        // Toggle related/not related property
        item.selected = item.selected ? false : true;
        if (item.selected) {
          let foundItem = false;
          _.forEach(this.selectedCopyObjects, function (object) {
            if (object.id == item.id) {
              foundItem = true
            }
          })

          if (!foundItem) {
            this.selectedCopyObjects.push(item)
          }
        } else {
          let itemIndex = _.findIndex(this.selectedCopyObjects, function(o) { return o.id == item.id; });
          this.selectedCopyObjects.splice(itemIndex, 1);
        }

        this.$set(this.copyItems, index, item)
      },
      selectAll() {
        let newDataObjectsArray = []
        let self = this;
        _.forEach(this.DOItems, function (item) {
          item.selected = true;
          newDataObjectsArray.push(item)
        })
        this.DOItems = newDataObjectsArray;
      },
      unselectAll() {
        let newDataObjectsArray = []
        let self = this;
        _.forEach(this.DOItems, function (item) {
          item.selected = false;
          newDataObjectsArray.push(item)
        })
        this.DOItems = newDataObjectsArray;
      },
      relateAll() {
        let self = this;
        let newDataObjectsArray = []
        _.forEach(this.allDataObjects, function (item, index) {
          self.handleDO(index, item, true)
        })
        if (this.selectedS && this.selectedS.id) {
          this.selectCopyItem(this.selectedS)
        } else {
          this.loadDO()
        }
      },
      unrelateAll() {
        let self = this;
        let newDataObjectsArray = []

        _.forEach(this.allDataObjects, function (item, index) {
          item.related = false
          self.$set(self.allDataObjects, index, item)
          self.$set(self.allDataObjectsFiltered, index, item)
        })

        _.forEach(this.allDataObjects, function (item, index) {
          if (item && !item.new_item) {
            newDataObjectsArray.push(item)
          }
        })

        this.allDataObjects = newDataObjectsArray
        this.allDataObjectsFiltered = _.cloneDeep(this.allDataObjects);

        if (this.selectedS && this.selectedS.id) {
          this.selectCopyItem(this.selectedS)
        } else {
          this.loadDO()
        }
      },
      updateDOList(selectedOption) {
        let self = this;
        let inArray = false;
        if (this.allDataObjects && this.allDataObjects.length) {
          _.forEach(this.allDataObjects, function (item, index) {
            if (item.id == selectedOption.id) {
              // if item is already in array
              item.related = true;
              self.$set(self.allDataObjects, index, item)
              inArray = true;
              self.allDataObjectsFiltered = _.cloneDeep(self.allDataObjects);
            }
          })
        }
        if (!inArray) {
          // If not in array, push it in array as related
          selectedOption.related = true;
          selectedOption.new_item = true;
          self.allDataObjects.push(selectedOption)
          self.allDataObjectsFiltered = _.cloneDeep(self.allDataObjects);
        }
      },
      removeDataItem(item, related) {

        // find indexes for both arrays
        let filteredIndex = _.findIndex(this.allDataObjectsFiltered, function(o) { return o.id == item.id; });
        let itemIndex = _.findIndex(this.allDataObjects, function(o) { return o.id == item.id; });

        // Remove item from data objects arrays
        this.allDataObjectsFiltered.splice(filteredIndex, 1);
        this.allDataObjects.splice(itemIndex, 1);

        if (this.selectedS && this.selectedS.id) {
          this.selectCopyItem(this.selectedS)
        } else {
          if (related == undefined) {
            this.loadDO()
          }
        }
      },
      showEditSModal(object) {
        let self = this;

        this.sForm.system_role = undefined
        this.sForm.rules_followed = object.rules_followed
        this.sForm.description = object.description

        if (object.system_role) {
          this.sForm.system_role = object.system_role
        }

        this.itemObject = object;
        this.$refs['edit-system'].show()
      },
      hideEditSModal() {
        this.systemRoleInput = ''
        this.$refs['edit-system'].hide()
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('activities/' + this.activity.slug + '/systems/' + object.id).then(function() {
          self.hideSConfirmDeleteModal();
          self.loadSystems();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('activities.system.delete.success'))
        }, function() {});
      },
      sFormSubmit(evt) {
        let formData = new FormData();
        let files = document.querySelector('#file');

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        if (evt) {
          evt.preventDefault();
        }

        formData.append("system_id", this.selectedS.id);
        formData.append("activity", this.activity.slug);
        this.buttonDisabled = true;
        if (this.selectedS && this.selectedS.id) {
          piincHttp.post('/activities/' + this.activity.slug + '/systems', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.$toastr('success', self.$t('activities.system.create.success'))
            self.hideCreateSModal();
            self.loadSystems();
            self.$emit('updated');
            self.buttonDisabled = false;
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
              if (error.data.errors.system_id) {
                self.error += ' ' + error.data.errors.system_id[0]
              }
              if (error.data.errors['files.0']) {
                self.error += ' ' + error.data.errors['files.0'][0]
              }
            }
          })
        } else {
          this.buttonDisabled = false;
          if (!this.selectedS || (this.selectedS && !this.selectedS.id)) {
            this.error +=  ' ' + this.$t('system.system_error_message')
          }
        }
      },
      dataMappingFormSubmit(evt) {

        let self = this;
        if (evt) {
          evt.preventDefault();
        }

        self.sForm.data_objects = []
        _.forEach(this.selectedDataObjects, function (dataObject) {
          if (dataObject.selected) {
            self.sForm.data_objects.push(dataObject.id)
          }
        })

        this.buttonDisabled = true;
        piincHttp.put('activities/' + this.activity.slug + '/systems/' + this.itemObject.id,
          {
            data_objects: this.sForm.data_objects
          }
        ).then(function(response) {
          self.$toastr('success', self.$t('activities.system.update.success'))
          self.$refs['data-mapping'].hide()
          self.copyThirdPartyItems = []
          self.$emit('updated');
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      copyFromFormSubmit(evt) {

        let self = this;
        if (evt) {
          evt.preventDefault();
        }

        _.forEach(this.selectedCopyObjects, function (dataObject) {
          if (_.findIndex(self.selectedDataObjects, function(o) { return o.id == dataObject.id; }) == -1) {
            self.selectedDataObjects.push(dataObject)
          }
        })

        self.$refs['copy-from'].hide()
        self.copyThirdPartyItems = []
      },
      sEditFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        self.sForm.data_objects = []
        _.forEach(this.itemObject.data_objects, function (dataObject) {
          self.sForm.data_objects.push(dataObject.id)
        })

        piincHttp.put('/activities/' + this.activity.slug + '/systems/' + this.itemObject.id, this.sForm).then(function(response) {
          self.$toastr('success', self.$t('activities.system.update.success'))
          self.hideEditSModal();
          self.loadSystems();
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })

        this.systemRoleInput = ''
      },
      handleValidationModalClose() {
        this.updateValidationList = true
        this.loadContent()
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'activities.view', params: { slug: object.model.slug }, query: { task: object.id }})
      },
      loadContent() {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.activity = response.data;
        }, function() {});
      },
      loadSystems() {
        let self = this;

        piincHttp.get('activities/' + this.activity.slug + '/systems', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.sTableItems = response.data;
          self.copySystemItems = []
          _.forEach(response.data, function (item) {
            item.name = item.system.name + ' - System'
            item.slug = item.system.slug
            self.copySystemItems.push(item)
          })

          self.sLoading = false;
        }, function() {});
      },
      loadS(query) {
        let self = this;
        piincHttp.get('systems', { params:
          {
            client: this.getClient.slug,
            activity: 1,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.sItems = response.data.items
          if (self.sItems && !self.sItems.length && !query) {
            self.sItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadDO(query, paginate) {
        let self = this;
        this.loading = true
        this.DOItems = []

        piincHttp.get('data-objects', { params:
          {
            client: this.getClient.slug,
            per_page: 200,
            page: this.page,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.DOItems = []
          let foundItem;

          _.forEach(response.data.items, function (item) {
            foundItem = _.find(self.selectedDataObjects, function(object) {
              return object.id == item.id
            });
            if (foundItem && foundItem.id) {
              item.selected = true
            }
            self.DOItems.push(item)
          })

          self.loading = false;
        }, function() {});
      },
      loadSR(query) {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug + '/systems/roles', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.systemRoleItems = response.data
          if (self.systemRoleItems && !self.systemRoleItems.length && !query) {
            self.systemRoleItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});

      }
    },
    created () {
      this.activity = this.object;
      if (this.activity && this.activity.id) {
        this.showTable = true;
        this.loadTasks('update_systems');
        this.loadSystems();
        this.loadSR();
      }
      if (this.getClient && this.getClient.slug) {

      }

      this.loadGuides('update_systems');
      this.loadGuides('copy_dataflow');
      this.loadGuides('data_mapping');

    }
  }
</script>
